import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { apiGet } from '../api';
import { theme, card, btnPrimary } from '../theme';
// v0.9.2 - trade palette feeds the decorative pins on the floorplan SVG.
import DropPinWordmark from '../components/DropPinWordmark';

// Landing screen. Shows the brand tagline, a backend health pill, and
// quick stats (project count, defect count). The first thing a tenant
// owner sees on login - directs them straight to /projects.
//
// v0.9.2 - tiles centralized + clickable (Projects -> /projects). Background
//          gets a subtle SVG floorplan-with-pins illustration so the empty
//          screen reads as DropPin's value-prop visually, not just textually.
//          (MEMORY: empty UI states never blank.)
export default function HomeScreen() {
  const navigate = useNavigate();
  const [health, setHealth] = useState({ state: 'checking' });
  const [stats, setStats] = useState(null);

  useEffect(() => {
    apiGet('/api/health')
      .then(d => setHealth({ state: d.ok ? 'ok' : 'error', ...d }))
      .catch(e => setHealth({ state: 'error', error: e.message }));
    // v0.45.24 — Single /api/stats call. Brett: "The tile count
    // squares take quite a while to load." The old code fired 1+2N
    // requests (one /api/projects + a /api/defects + /api/reports
    // for every project) and only painted the tiles once ALL
    // resolved. New endpoint returns the five numbers in one DB
    // round-trip via batched COUNTs on indexed columns.
    apiGet('/api/stats')
      .then(s => setStats(s))
      .catch(() => setStats(null));
  }, []);

  return (
    // v0.9.6 - Full-bleed hero. The AppShell wraps <main> in a 1200px
    // max-width container, which was clipping the blueprint into a small
    // card. The outer div uses the "100vw margin trick" to escape that
    // container and span the full viewport width. minHeight bumped from
    // 70vh -> 88vh so the blueprint actually fills the screen the way
    // Brett briefed (cover ~75% of the page on an angle).
    <div style={{
      position: 'relative',
      minHeight: 'calc(100vh - 56px)',
      width: '100vw',
      marginLeft: 'calc(-50vw + 50%)',
      // v0.9.9 - Negate the AppShell <main>'s 28px padding-top/bottom
      // so the blueprint hero butts against the bottom of the menu
      // bar with no cream strip in between (Brett: "White gaps still
      // there"). The v0.9.8 viewBox trim moved the SVG content up;
      // this trick moves the SVG CONTAINER up.
      marginTop: '-28px',
      marginBottom: '-28px',
      overflow: 'hidden',
    }}>
      {/* v0.9.5 - decorative SVG behind the hero. preserveAspectRatio
          "xMidYMid slice" so it covers the whole region (cropping the
          edges rather than letterboxing). pointerEvents:none keeps the
          buttons/tiles above it interactive. */}
      <PlanViewBackground />

      <div style={{ position: 'relative', textAlign: 'center', padding: '40px 24px 48px', maxWidth: 1200, margin: '0 auto' }}>
        {/* v0.10.3 - Soft cream backdrop panel behind the text block.
            v0.9.9's text-shadow glow looked bad and the sub-paragraph
            was invisible against the blueprint (Brett: "i dont like
            the glow and you cant see the text about the fastest
            defects"). A semi-transparent cream card kills the glow
            and gives the headline area a clear stage. */}
        <div style={{
          display: 'inline-block',
          background: 'rgba(252, 249, 238, 0.92)',
          border: '1px solid rgba(216, 207, 168, 0.6)',
          borderRadius: 14,
          // v0.11.0 - responsive padding (clamp) + width:min so the
          // card never overflows on a phone but still caps nicely on
          // desktop.
          padding: 'clamp(18px, 4vw, 24px) clamp(20px, 6vw, 36px)',
          boxShadow: '0 6px 24px rgba(15, 23, 42, 0.18)',
          maxWidth: 640,
          width: 'min(640px, calc(100% - 24px))',
        }}>
          <div style={{ marginBottom: 12 }}>
            <span style={{
              fontSize: 52, fontWeight: 900, letterSpacing: '-0.02em',
              color: theme.brand,
              fontFamily: 'inherit',
              lineHeight: 1,
            }}><DropPinWordmark /></span>
          </div>
          <h1 style={{
            fontSize: 30, fontWeight: 800, margin: '0 0 12px',
            letterSpacing: '-0.02em',
            color: theme.text,
          }}>
            Drop a plan. Drop a pin. Send a report.
          </h1>
          <p style={{
            fontSize: 14, color: theme.text, maxWidth: 520, margin: '0 auto',
            lineHeight: 1.5,
            opacity: 0.85,
          }}>
            The fastest defects / snag / punch-list tool for trades, builders,
            surveyors and property managers.
          </p>

          <div style={{ marginTop: 18, display: 'inline-flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'center' }}>
            <button onClick={() => navigate('/projects')} style={{ ...btnPrimary, padding: '12px 20px', fontSize: 14 }}>
              Open Locations &rarr;
            </button>
          </div>
        </div>
      </div>

      {/* v0.9.2 - centred flex row of fixed-width tiles (was: auto-fill grid).
          v0.45.24 — Tiles always render. Numbers come from /api/stats
          (single round trip). While loading, value is "—" so the layout
          locks in immediately — no slow-load blank space. */}
      <div style={{
        position: 'relative',
        display: 'flex', justifyContent: 'center',
        gap: 14, flexWrap: 'wrap', marginTop: 32,
        maxWidth: 1200, margin: '32px auto 0',
      }}>
        <StatTile label="Locations" value={stats ? stats.projects : '—'} accent={theme.brand}
          onClick={() => navigate('/projects')} />
        <StatTile label="Defects open" accent={theme.warn} icon="pin"
          value={stats ? (stats.defectsTotal > 0 ? String(stats.defectsOpen) : '0') : '—'}
          caption={stats
            ? (stats.defectsTotal > 0
                ? `${stats.defectsRectified} done of ${stats.defectsTotal} total`
                : 'no defects yet')
            : 'loading…'}
        />
        <StatTile label="Reports" value={stats ? stats.reports : '—'} accent={theme.success}
          caption={stats
            ? (stats.reports === 1 ? 'saved report' : 'saved reports')
            : 'loading…'} />
      </div>
    </div>
  );
}

function HealthPill({ health }) {
  const dot =
    health.state === 'ok'    ? theme.success :
    health.state === 'error' ? theme.danger  :
                               theme.warn;
  const msg =
    health.state === 'checking' ? 'checking backend...' :
    health.state === 'ok'       ? `backend ok - db ${health.dbConnected ? 'connected' : 'not connected'}` :
                                  `backend error: ${health.error}`;
  return (
    <span style={{
      padding: '8px 14px',
      background: theme.surface,
      border: `1px solid ${theme.border}`,
      borderRadius: 999,
      fontSize: 12,
      fontFamily: 'monospace',
      display: 'inline-flex',
      alignItems: 'center',
      gap: 8,
    }}>
      <span style={{ width: 8, height: 8, borderRadius: '50%', background: dot, display: 'inline-block' }} />
      <span>{msg}</span>
    </span>
  );
}

// v0.9.2 - StatTile. Accepts optional onClick. When clickable: pointer cursor,
// hover lift, brand-tinted accent border on hover. When static: cursor:default,
// no hover effect. Width is fixed so the row reads as a deliberate trio rather
// than auto-stretching to fill the container.
function TileIcon({ name, accent }) {
  // v0.9.10 - Two inline SVG glyphs the StatTile can render: 'plan'
  // (a blueprint thumbnail with a small map-pin sticker on it) and
  // 'pin' (a 3D-looking map pin head + shaft, matching the SVG pins
  // on the blueprint hero so the visual vocabulary is consistent).
  if (!name) return null;
  if (name === 'plan') {
    return (
      <svg viewBox="0 0 40 32" width="40" height="32" aria-hidden="true">
        {/* paper sheet */}
        <rect x="2" y="3" width="36" height="26" rx="1.5"
              fill="#0e3a6b" stroke="#0a2a52" strokeWidth="0.5" />
        {/* grid */}
        <g stroke="#5fa3d6" strokeWidth="0.35" opacity="0.5">
          <line x1="2"  y1="10" x2="38" y2="10" />
          <line x1="2"  y1="16" x2="38" y2="16" />
          <line x1="2"  y1="22" x2="38" y2="22" />
          <line x1="11" y1="3"  x2="11" y2="29" />
          <line x1="20" y1="3"  x2="20" y2="29" />
          <line x1="29" y1="3"  x2="29" y2="29" />
        </g>
        {/* floor plan rooms */}
        <g stroke="#cfe4f5" fill="none" strokeWidth="0.9">
          <path d="M 6 8 L 6 25 L 34 25 L 34 14 L 26 14 L 26 8 Z" />
          <line x1="6"  y1="17" x2="20" y2="17" />
          <line x1="20" y1="17" x2="20" y2="25" />
        </g>
        {/* tiny pin marker on the plan */}
        <g transform="translate(15 13)">
          <circle r="2.4" fill={accent} stroke="#7c2d12" strokeWidth="0.4" />
          <path d="M 0 2 L 0 5 L -1.5 3 Z" fill={accent} />
        </g>
      </svg>
    );
  }
  if (name === 'pin') {
    return (
      <svg viewBox="0 0 32 40" width="32" height="40" aria-hidden="true">
        <defs>
          <radialGradient id="tile-pin-head" cx="35%" cy="30%" r="70%">
            <stop offset="0"    stopColor="#ff9a4d" />
            <stop offset="0.6"  stopColor="#e8650a" />
            <stop offset="1"    stopColor="#7c2d12" />
          </radialGradient>
          <linearGradient id="tile-pin-shaft" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0"   stopColor="#d4d4d8" />
            <stop offset="0.5" stopColor="#9ca3af" />
            <stop offset="1"   stopColor="#374151" />
          </linearGradient>
        </defs>
        {/* shadow */}
        <ellipse cx="16" cy="36" rx="7" ry="1.5" fill="#000" opacity="0.25" />
        {/* shaft */}
        <path d="M 14 14 L 16 36 L 18 14 Z" fill="url(#tile-pin-shaft)"
              stroke="#1f2937" strokeWidth="0.4" />
        {/* head */}
        <circle cx="16" cy="11" r="9" fill="url(#tile-pin-head)"
                stroke="#7c2d12" strokeWidth="0.7" />
        {/* specular highlight */}
        <ellipse cx="13" cy="8" rx="2.5" ry="2" fill="#fff" opacity="0.55" />
      </svg>
    );
  }
  return null;
}

function StatTile({ label, value, accent, onClick, icon, caption }) {
  const [hover, setHover] = useState(false);
  const clickable = typeof onClick === 'function';
  return (
    <div
      onClick={clickable ? onClick : undefined}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        ...card,
        borderLeft: `4px solid ${accent}`,
        width: 200,
        cursor: clickable ? 'pointer' : 'default',
        transform: clickable && hover ? 'translateY(-2px)' : 'none',
        boxShadow: clickable && hover ? '0 6px 14px rgba(0,0,0,0.08)' : (card.boxShadow || 'none'),
        transition: 'transform 120ms ease, box-shadow 120ms ease',
        userSelect: 'none',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 11, color: theme.textLight, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            {label}{clickable && <span style={{ marginLeft: 6, color: accent, opacity: hover ? 1 : 0.5 }}>&rarr;</span>}
          </div>
          <div style={{ fontSize: 28, fontWeight: 800, marginTop: 4, fontFamily: 'monospace' }}>{value}</div>
          {/* v0.10.3 - optional small caption under the value. */}
          {caption && (
            <div style={{ fontSize: 11, color: theme.textLight, marginTop: 2 }}>{caption}</div>
          )}
        </div>
        {icon && (
          <div style={{ flex: '0 0 auto', marginTop: 2 }}>
            <TileIcon name={icon} accent={accent} />
          </div>
        )}
      </div>
    </div>
  );
}

// v0.9.5 - PlanViewBackground rebuilt. Brett's brief: "old school blue
// print covering 3/4 of the page with pins looking on an angle so pins
// look 3D" + a fast-food restaurant layout (his field) + a handwritten
// defect note poking out from underneath the blueprint.
// Composed on a 1200x600 viewBox to match the existing canvas:
//   - Cream paper background (the part NOT covered by blueprint)
//   - Cyan blueprint sheet, slightly rotated, covering the upper-right
//     ~75% with a typical McD-style floor plan (kitchen / dining /
//     McCafe / drive-thru / toilets / PlayPlace / freezer)
//   - Title block, dimension lines, room labels, north arrow, scale bar,
//     hood-canopy callout - reads as a real working drawing
//   - 4 angled pins (red / orange / green / yellow) with shadow ellipse
//     + specular highlight for the 3D effect
//   - An exposed corner of lined notepaper underneath with a hand-written
//     snag list
// pointerEvents:none + zIndex:0 so the HTML hero (wordmark, tagline,
// tiles) floats above without click interference. Opacity bumped to 0.85
// so the blueprint reads clearly - Brett wanted it as a hero element,
// not a faint decoration.
function PlanViewBackground() {
  return (
    <svg
      viewBox="-140 38 1480 772"
      preserveAspectRatio="xMidYMin slice"
      aria-hidden="true"
      style={{
        position: 'absolute', inset: 0, width: '100%', height: '100%',
        opacity: 0.95, pointerEvents: 'none', zIndex: 0,
      }}
    >
      <defs>
        <pattern id="bp-grid-fine" x="0" y="0" width="32" height="32" patternUnits="userSpaceOnUse">
          <path d="M 32 0 L 0 0 0 32" fill="none" stroke="#3a7eb8" strokeWidth="0.6" opacity="0.35" />
        </pattern>
        <pattern id="bp-grid-coarse" x="0" y="0" width="160" height="160" patternUnits="userSpaceOnUse">
          <path d="M 160 0 L 0 0 0 160" fill="none" stroke="#5fa3d6" strokeWidth="0.9" opacity="0.45" />
        </pattern>
        <radialGradient id="bp-vignette" cx="0.4" cy="0.35" r="0.9">
          <stop offset="0" stopColor="#1a4d83" stopOpacity="0" />
          <stop offset="1" stopColor="#072448" stopOpacity="0.55" />
        </radialGradient>
        <linearGradient id="pin-red" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0"    stopColor="#ff6b6b" />
          <stop offset="0.55" stopColor="#dc2626" />
          <stop offset="1"    stopColor="#7f1d1d" />
        </linearGradient>
        <linearGradient id="pin-orange" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0"    stopColor="#ffa959" />
          <stop offset="0.55" stopColor="#e8650a" />
          <stop offset="1"    stopColor="#7c2d12" />
        </linearGradient>
        <linearGradient id="pin-green" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0"    stopColor="#6ee7b7" />
          <stop offset="0.55" stopColor="#16a34a" />
          <stop offset="1"    stopColor="#14532d" />
        </linearGradient>
        <linearGradient id="pin-yellow" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0"    stopColor="#fde68a" />
          <stop offset="0.55" stopColor="#eab308" />
          <stop offset="1"    stopColor="#713f12" />
        </linearGradient>
        <linearGradient id="pin-shaft" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0"   stopColor="#d4d4d8" />
          <stop offset="0.5" stopColor="#9ca3af" />
          <stop offset="1"   stopColor="#374151" />
        </linearGradient>
      </defs>

      {/* v0.9.7 - Cream desk surface fills the expanded viewBox.
          Subtle radial vignette so the corners feel slightly worn /
          shadowed, selling the "supervisor's desk" framing rather
          than a flat coloured background. */}
      <defs>
        <radialGradient id="desk-vignette" cx="0.5" cy="0.55" r="0.85">
          <stop offset="0"    stopColor="#fcf9ee" />
          <stop offset="0.65" stopColor="#f3eed8" />
          <stop offset="1"    stopColor="#d8cfa8" />
        </radialGradient>
        <filter id="paper-shadow" x="-10%" y="-10%" width="120%" height="120%">
          <feGaussianBlur in="SourceAlpha" stdDeviation="10" />
          <feOffset dx="4" dy="14" result="offsetBlur" />
          <feComponentTransfer><feFuncA type="linear" slope="0.55" /></feComponentTransfer>
          <feMerge>
            <feMergeNode />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>
      <rect x="-140" y="38" width="1480" height="772" fill="url(#desk-vignette)" />

      {/* v0.9.7 - Blueprint sheet, lightly rotated, with a soft drop
          shadow so it reads as a piece of paper laid on the desk. */}
      <g transform="rotate(-2 640 360)" filter="url(#paper-shadow)">
        <rect x="200" y="40" width="960" height="680" fill="#0e3a6b" stroke="#0a2a52" strokeWidth="0.8" />
        <rect x="200" y="40" width="960" height="680" fill="url(#bp-grid-fine)" />
        <rect x="200" y="40" width="960" height="680" fill="url(#bp-grid-coarse)" />
        <rect x="200" y="40" width="960" height="680" fill="url(#bp-vignette)" />

        {/* Title block */}
        <g transform="translate(232 64)">
          <rect width="360" height="70" fill="none" stroke="#9bc7e8" strokeWidth="0.8" />
          <text x="14" y="22" fontFamily="Courier New, monospace" fontSize="14" fill="#cfe4f5" letterSpacing="2">DRAWING NO.</text>
          <text x="14" y="48" fontFamily="Courier New, monospace" fontSize="20" fill="#ffffff" fontWeight="500">A-101  GROUND FLOOR</text>
          <text x="14" y="62" fontFamily="Courier New, monospace" fontSize="12" fill="#9bc7e8">FAST-FOOD RESTAURANT  -  REV 03</text>
        </g>

        {/* Building envelope */}
        <g stroke="#cfe4f5" fill="none" strokeWidth="2.4" strokeLinecap="square">
          <path d="M 300 200 L 300 540 L 900 540 L 900 380 L 1040 380 L 1040 200 Z" />
        </g>

        {/* Internal partitions */}
        <g stroke="#cfe4f5" fill="none" strokeWidth="1.8">
          <line x1="300" y1="380" x2="740" y2="380" />
          <line x1="480" y1="200" x2="480" y2="380" />
          <line x1="480" y1="280" x2="380" y2="280" />
          <line x1="740" y1="380" x2="740" y2="480" />
          <line x1="740" y1="200" x2="740" y2="380" />
          <line x1="740" y1="280" x2="900" y2="280" />
          <line x1="300" y1="480" x2="440" y2="480" />
          <line x1="380" y1="480" x2="380" y2="540" />
          <line x1="800" y1="480" x2="800" y2="540" />
          <line x1="800" y1="480" x2="900" y2="480" />
        </g>

        {/* Door swings */}
        <g stroke="#9bc7e8" fill="none" strokeWidth="0.9" strokeDasharray="3 3">
          <path d="M 560 540 A 26 26 0 0 1 560 514" />
          <path d="M 640 540 A 26 26 0 0 0 640 514" />
          <path d="M 340 480 A 18 18 0 0 1 340 462" />
          <path d="M 410 480 A 18 18 0 0 1 410 462" />
          <path d="M 640 380 A 22 22 0 0 0 640 402" />
        </g>

        {/* Door gaps */}
        <g stroke="#0e3a6b" strokeWidth="3.6">
          <line x1="560" y1="540" x2="585" y2="540" />
          <line x1="615" y1="540" x2="640" y2="540" />
          <line x1="340" y1="480" x2="358" y2="480" />
          <line x1="410" y1="480" x2="428" y2="480" />
          <line x1="640" y1="380" x2="662" y2="380" />
        </g>

        {/* Servery counter detail */}
        <g stroke="#9bc7e8" fill="none" strokeWidth="0.7" strokeDasharray="4 4">
          <line x1="680" y1="388" x2="740" y2="388" />
          <line x1="680" y1="410" x2="740" y2="410" />
          <line x1="680" y1="432" x2="740" y2="432" />
        </g>

        {/* Kitchen cook-line fixtures */}
        <g stroke="#9bc7e8" fill="none" strokeWidth="0.8">
          <rect x="495" y="210" width="38" height="22" />
          <rect x="540" y="210" width="38" height="22" />
          <rect x="585" y="210" width="38" height="22" />
          <rect x="630" y="210" width="38" height="22" />
          <rect x="675" y="210" width="38" height="22" />
          <rect x="495" y="240" width="218" height="16" />
          <rect x="495" y="265" width="218" height="22" />
        </g>

        {/* Freezer hatch */}
        <g stroke="#9bc7e8" strokeWidth="0.6" opacity="0.6">
          <line x1="310" y1="215" x2="470" y2="270" />
          <line x1="310" y1="240" x2="470" y2="270" />
          <line x1="310" y1="265" x2="470" y2="270" />
        </g>

        {/* Dining seating booths */}
        <g stroke="#9bc7e8" fill="none" strokeWidth="0.7">
          <rect x="320" y="498" width="28" height="22" />
          <rect x="450" y="498" width="28" height="22" />
          <rect x="490" y="498" width="28" height="22" />
          <rect x="530" y="498" width="28" height="22" />
          <rect x="650" y="498" width="28" height="22" />
          <rect x="690" y="498" width="28" height="22" />
          <rect x="730" y="498" width="28" height="22" />
          {/* 4-top tables */}
          <rect x="490" y="420" width="22" height="22" />
          <rect x="535" y="420" width="22" height="22" />
          <rect x="580" y="420" width="22" height="22" />
          <rect x="625" y="420" width="22" height="22" />
          <rect x="490" y="455" width="22" height="22" />
          <rect x="535" y="455" width="22" height="22" />
          <rect x="580" y="455" width="22" height="22" />
          <rect x="625" y="455" width="22" height="22" />
        </g>

        {/* PlayPlace */}
        <g stroke="#9bc7e8" fill="none" strokeWidth="0.7">
          <circle cx="835" cy="500" r="10" />
          <circle cx="855" cy="515" r="13" />
          <circle cx="875" cy="498" r="8" />
          <rect x="825" y="488" width="12" height="12" />
        </g>

        {/* McCafe counter */}
        <g stroke="#9bc7e8" fill="none" strokeWidth="0.9">
          <line x1="755" y1="240" x2="890" y2="240" />
          <rect x="760" y="210" width="22" height="18" />
          <rect x="790" y="210" width="22" height="18" />
          <rect x="820" y="210" width="22" height="18" />
        </g>

        {/* Drive-thru lane */}
        <g stroke="#9bc7e8" fill="none" strokeWidth="0.9" strokeDasharray="8 5">
          <path d="M 1040 340 L 1040 200 L 950 110 L 830 110 L 730 160 L 640 200" />
          <line x1="1040" y1="270" x2="1070" y2="270" />
          <line x1="1040" y1="310" x2="1070" y2="310" />
        </g>
        <text x="1060" y="276" fontFamily="Courier New, monospace" fontSize="11" fill="#cfe4f5">ORDER</text>
        <text x="1060" y="316" fontFamily="Courier New, monospace" fontSize="11" fill="#cfe4f5">PICK-UP</text>

        {/* Room labels */}
        <g fontFamily="Courier New, monospace" fontSize="13" fill="#9bc7e8" letterSpacing="1.5">
          <text x="350" y="350">FREEZER</text>
          <text x="320" y="350" fontSize="11" fill="#7fb1d8"></text>
          <text x="560" y="350">KITCHEN</text>
          <text x="780" y="350">COFFEE</text>
          <text x="950" y="290">DRIVE-THRU</text>
          <text x="540" y="460" letterSpacing="3">DINING</text>
          <text x="335" y="514" fontSize="12">WC</text>
          <text x="395" y="514" fontSize="12">WC</text>
          <text x="826" y="525" fontSize="12">PLAY</text>
          <text x="585" y="568" fontSize="11" fill="#cfe4f5" fontWeight="500">ENTRY</text>
        </g>

        {/* Dimension top */}
        <g stroke="#7fb1d8" strokeWidth="0.7" fill="none">
          <line x1="300" y1="175" x2="1040" y2="175" />
          <line x1="300" y1="167" x2="300" y2="183" />
          <line x1="1040" y1="167" x2="1040" y2="183" />
        </g>
        <text x="670" y="167" fontFamily="Courier New, monospace" fontSize="13" fill="#cfe4f5" textAnchor="middle">28 400</text>

        {/* North arrow */}
        <g transform="translate(1020 100)">
          <circle r="20" fill="none" stroke="#cfe4f5" strokeWidth="0.9" />
          <path d="M 0 -14 L 6 12 L 0 6 L -6 12 Z" fill="#cfe4f5" />
          <text x="0" y="-26" fontFamily="Courier New, monospace" fontSize="12" fill="#cfe4f5" textAnchor="middle">N</text>
        </g>

        {/* Scale bar */}
        <g transform="translate(820 640)">
          <text x="0" y="0" fontFamily="Courier New, monospace" fontSize="11" fill="#9bc7e8">SCALE 1:100</text>
          <g transform="translate(0 10)">
            <rect x="0"  y="0" width="30" height="6" fill="#cfe4f5" />
            <rect x="30" y="0" width="30" height="6" fill="none" stroke="#cfe4f5" strokeWidth="0.5" />
            <rect x="60" y="0" width="30" height="6" fill="#cfe4f5" />
            <rect x="90" y="0" width="30" height="6" fill="none" stroke="#cfe4f5" strokeWidth="0.5" />
          </g>
          <text x="0"  y="32" fontFamily="Courier New, monospace" fontSize="10" fill="#9bc7e8">0</text>
          <text x="60" y="32" fontFamily="Courier New, monospace" fontSize="10" fill="#9bc7e8">2m</text>
          <text x="120" y="32" fontFamily="Courier New, monospace" fontSize="10" fill="#9bc7e8">4m</text>
        </g>

        {/* Callout */}
        <g stroke="#9bc7e8" strokeWidth="0.7" fill="none">
          <line x1="560" y1="320" x2="730" y2="100" />
          <circle cx="730" cy="100" r="4" fill="#9bc7e8" />
        </g>
        <text x="738" y="98" fontFamily="Courier New, monospace" fontSize="12" fill="#cfe4f5">HOOD CANOPY  -  REF M-04</text>
        <text x="738" y="114" fontFamily="Courier New, monospace" fontSize="11" fill="#9bc7e8">FIRE SUPPRESSION TO AS 1851</text>
      </g>

      {/* v0.9.7 - Snag note repositioned. Sits at the bottom-left of
           the cream surface, fully visible, partially tucked under
           the blueprint's bottom-left corner (so it reads as the
           supervisor's notebook lying next to the drawing). Drop
           shadow + slight extra rotation for the "thrown down on the
           desk" feel. */}
      <g transform="rotate(6 -70 580)" filter="url(#paper-shadow)">
        <rect x="-120" y="500" width="430" height="195" fill="#f4eed9" stroke="#c9c3a8" strokeWidth="0.8" />
        <g stroke="#c9c0a3" strokeWidth="0.6" opacity="0.7">
          <line x1="-98" y1="530" x2="290" y2="530" />
          <line x1="-98" y1="558" x2="290" y2="558" />
          <line x1="-98" y1="586" x2="290" y2="586" />
          <line x1="-98" y1="614" x2="290" y2="614" />
          <line x1="-98" y1="642" x2="290" y2="642" />
          <line x1="-98" y1="670" x2="290" y2="670" />
        </g>
        <line x1="-60" y1="502" x2="-60" y2="695" stroke="#c0726f" strokeWidth="0.9" opacity="0.7" />
        <g fontFamily="Comic Sans MS, Bradley Hand, cursive" fill="#1f2a44">
          <text x="-44" y="528" fontSize="22" fontWeight="500">Snag list  -  Bayside Burgers</text>
          <text x="-44" y="558" fontSize="18">#1  Kitchen tile grout cracked x3</text>
          <text x="-44" y="586" fontSize="18">#2  Coffee bar skirting chipped</text>
          <text x="-44" y="614" fontSize="18">#3  Drive-thru window seal leak</text>
          <text x="-44" y="642" fontSize="18">#4  Play area soft fall scuffed</text>
        </g>
        <path d="M -44 668 Q -20 660 10 670 T 70 666" stroke="#1f2a44" strokeWidth="1.6" fill="none" strokeLinecap="round" />
      </g>

      {/* v0.9.7 - Yellow pencil resting across the bottom-right of the
           blueprint. Six-sided body simplified to rounded rect, with a
           metal ferrule, pink eraser, and sharpened graphite tip. Adds
           the "supervisor's desk" scene cue without overdoing it. */}
      <g transform="translate(1090 660) rotate(-22)" filter="url(#paper-shadow)">
        <ellipse cx="0" cy="20" rx="120" ry="3.5" fill="#000" opacity="0.18" />
        <rect x="-110" y="-7" width="180" height="14" rx="2" fill="#f5b800" stroke="#854d0e" strokeWidth="0.7" />
        <rect x="-110" y="-7" width="180" height="3.5" fill="#fde68a" opacity="0.55" />
        <rect x="70" y="-8" width="16" height="16" fill="#cbd5e1" stroke="#475569" strokeWidth="0.7" />
        <line x1="73" y1="-7" x2="73" y2="7" stroke="#94a3b8" strokeWidth="0.6" />
        <line x1="78" y1="-7" x2="78" y2="7" stroke="#94a3b8" strokeWidth="0.6" />
        <line x1="83" y1="-7" x2="83" y2="7" stroke="#94a3b8" strokeWidth="0.6" />
        <rect x="86" y="-7" width="18" height="14" rx="2" fill="#ef4444" stroke="#7f1d1d" strokeWidth="0.7" />
        <path d="M -110 -7 L -132 0 L -110 7 Z" fill="#deb887" stroke="#7c2d12" strokeWidth="0.7" />
        <path d="M -126 -2 L -132 0 L -126 2 Z" fill="#1f2937" />
      </g>

      {/* ANGLED PIN 1 - red, on kitchen tile area */}
      <g transform="translate(560 340) rotate(-22)">
        <ellipse cx="9" cy="48" rx="20" ry="4" fill="#000" opacity="0.35" />
        <path d="M 0 0 L 3 48 L -3 48 Z" fill="url(#pin-shaft)" stroke="#1f2937" strokeWidth="0.6" />
        <circle cx="0" cy="-3" r="16" fill="url(#pin-red)" stroke="#7f1d1d" strokeWidth="0.9" />
        <ellipse cx="-4" cy="-7" rx="4" ry="3" fill="#fff" opacity="0.55" />
      </g>

      {/* PIN 2 - orange, on McCafe skirting */}
      <g transform="translate(820 330) rotate(15)">
        <ellipse cx="-6" cy="48" rx="20" ry="4" fill="#000" opacity="0.35" />
        <path d="M 0 0 L 3 48 L -3 48 Z" fill="url(#pin-shaft)" stroke="#1f2937" strokeWidth="0.6" />
        <circle cx="0" cy="-3" r="16" fill="url(#pin-orange)" stroke="#7c2d12" strokeWidth="0.9" />
        <ellipse cx="-4" cy="-7" rx="4" ry="3" fill="#fff" opacity="0.55" />
      </g>

      {/* PIN 3 - green, on drive-thru window */}
      <g transform="translate(990 300) rotate(-10)">
        <ellipse cx="3" cy="48" rx="20" ry="4" fill="#000" opacity="0.35" />
        <path d="M 0 0 L 3 48 L -3 48 Z" fill="url(#pin-shaft)" stroke="#1f2937" strokeWidth="0.6" />
        <circle cx="0" cy="-3" r="16" fill="url(#pin-green)" stroke="#14532d" strokeWidth="0.9" />
        <ellipse cx="-4" cy="-7" rx="4" ry="3" fill="#fff" opacity="0.55" />
      </g>

      {/* PIN 4 - yellow, on PlayPlace floor */}
      <g transform="translate(870 510) rotate(28)">
        <ellipse cx="-12" cy="48" rx="20" ry="4" fill="#000" opacity="0.35" />
        <path d="M 0 0 L 3 48 L -3 48 Z" fill="url(#pin-shaft)" stroke="#1f2937" strokeWidth="0.6" />
        <circle cx="0" cy="-3" r="16" fill="url(#pin-yellow)" stroke="#713f12" strokeWidth="0.9" />
        <ellipse cx="-4" cy="-7" rx="4" ry="3" fill="#fff" opacity="0.55" />
      </g>
    </svg>
  );
}
